/**
 * ROOFTOPS family — the CITY AROUND THE BLOCK (pure layout, no three.js).
 *
 * Before this the two day rooftop maps were a handful of buildings standing in
 * an empty plain with mountains behind: from any roof you looked straight past
 * the last parapet at forest. A rooftop map is only believable inside a city.
 * This lays a ring of city blocks — street grid, mixed heights rising toward
 * the back, cornices, roof caps, rooftop plant and the classic timber water
 * tanks on steel legs — entirely OUTSIDE the map bounds (unreachable: the
 * bench asserts every piece is ≥ SKYLINE_MARGIN beyond `map.bounds`), so tall
 * props are fine out there. The spiderweb variant keeps decor.ts's synthwave
 * skyline instead.
 */
import type { MapDef } from "@neon-purge/shared";
import { h01, type RoofPieceX } from "./rooftopsLayout.js";

export const SKYLINE_MARGIN = 4;

const FACADE_TINTS = [0xcdc3b2, 0xb07a5f, 0x9d9c98, 0xd6c7a3, 0x8f8a84, 0xc3ad94, 0xa55f4b];

export function skylineLayout(map: MapDef, lowSpec: boolean): RoofPieceX[] {
  const out: RoofPieceX[] = [];
  const bx0 = map.bounds.min[0], bx1 = map.bounds.max[0], bz0 = map.bounds.min[2], bz1 = map.bounds.max[2];
  const inner = Math.max(bx1, -bx0, bz1, -bz0) + SKYLINE_MARGIN + 3;
  const span = inner < 60 ? 52 : 64;
  const CELL = 23, STREET = 7;
  const outer = inner + span;
  const mk = (p: Partial<RoofPieceX> & Pick<RoofPieceX, "mat" | "x" | "y" | "z" | "sx" | "sy" | "sz" | "color">): RoofPieceX =>
    ({ zone: "skyline", shape: "box", axis: "y", taper: 1, host: -1, ...p } as RoofPieceX);
  const outside = (x0: number, x1: number, z0: number, z1: number): boolean =>
    x0 > bx1 + SKYLINE_MARGIN || x1 < bx0 - SKYLINE_MARGIN || z0 > bz1 + SKYLINE_MARGIN || z1 < bz0 - SKYLINE_MARGIN;
  const base = -8;
  const n = Math.ceil(outer / CELL);
  for (let i = -n; i <= n; i++) for (let j = -n; j <= n; j++) {
    if (lowSpec && ((i + j) & 1)) continue;
    const cx = i * CELL, cz = j * CELL;
    const ring = Math.max(Math.abs(cx), Math.abs(cz));
    if (ring > outer) continue;
    // a city block = 1..3 buildings sharing the block, street on every side
    const blk = CELL - STREET;
    const split = h01(i, j, 1) < 0.45 ? 1 : h01(i, j, 2) < 0.6 ? 2 : 3;
    for (let k = 0; k < split; k++) {
      const alongX = h01(i, j, 3) < 0.5;
      const len = blk / split;
      const w = alongX ? len - 0.6 : blk - h01(i, j, 10 + k) * 3;
      const d = alongX ? blk - h01(i, j, 10 + k) * 3 : len - 0.6;
      const x = alongX ? cx - blk / 2 + len * (k + 0.5) : cx;
      const z = alongX ? cz : cz - blk / 2 + len * (k + 0.5);
      if (!outside(x - w / 2, x + w / 2, z - d / 2, z + d / 2)) continue;
      const far = Math.max(0, Math.max(Math.abs(x), Math.abs(z)) - inner) / span;
      const hgt = 9 + h01(i, j, 20 + k) * 13 + far * 26 + (h01(i, j, 30 + k) < 0.12 ? 18 : 0);
      const top = Math.round(hgt / 1.125) * 1.125 + 0.5; // land the cornice between window rows
      const tint = FACADE_TINTS[Math.floor(h01(i, j, 40 + k) * FACADE_TINTS.length)];
      out.push(mk({ mat: "facade", x, y: (base + top) / 2, z, sx: w, sy: top - base, sz: d, color: tint }));
      // cornice band + roof cap + parapet coping
      out.push(mk({ mat: "roof", x, y: top + 0.12, z, sx: w + 0.5, sy: 0.24, sz: d + 0.5, color: 0x8d8a84 }));
      out.push(mk({ mat: "roof", x, y: top + 0.27, z, sx: w - 0.2, sy: 0.06, sz: d - 0.2, color: 0x5e5a54 }));
      // rooftop plant: a stair bulkhead and an AC block
      const s1 = h01(i, j, 50 + k), s2 = h01(i, j, 60 + k);
      const hx = x + (s1 - 0.5) * (w - 4), hz = z + (s2 - 0.5) * (d - 4);
      out.push(mk({ mat: "facade", x: hx, y: top + 1.4, z: hz, sx: 2.6, sy: 2.4, sz: 3.2, color: tint }));
      out.push(mk({ mat: "roof", x: x - (s1 - 0.5) * (w - 4), y: top + 0.85, z: z + (s2 - 0.5) * 2, sx: 2.2, sy: 1.2, sz: 1.6, color: 0xa9aca8 }));
      // the classic timber water tank on a steel stand (~40 % of roofs)
      if (!lowSpec && h01(i, j, 70 + k) < 0.4 && w > 8 && d > 8) {
        const tx = x - (s1 - 0.5) * (w - 6), tz = z - (s2 - 0.5) * (d - 6);
        const legH = 2.2, tH = 3.2, tD = 3.0;
        for (const [ox, oz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
          out.push(mk({ mat: "tank", x: tx + ox * 1.0, y: top + 0.3 + legH / 2, z: tz + oz * 1.0, sx: 0.16, sy: legH, sz: 0.16, color: 0x4a4a48 }));
        }
        out.push(mk({ mat: "tank", x: tx, y: top + 0.3 + legH, z: tz, sx: 3.0, sy: 0.18, sz: 3.0, color: 0x4a4a48 }));
        out.push(mk({ mat: "tank", shape: "cyl", x: tx, y: top + 0.3 + legH + 0.09 + tH / 2, z: tz, sx: tD, sy: tH, sz: tD, color: 0x8a6a4c }));
        out.push(mk({ mat: "tank", shape: "cyl", taper: 0.08, x: tx, y: top + 0.3 + legH + 0.09 + tH + 0.6, z: tz, sx: tD + 0.2, sy: 1.2, sz: tD + 0.2, color: 0x5d4a3a }));
        // steel hoops
        for (const hy of [0.5, 1.6, 2.7]) {
          out.push(mk({ mat: "tank", shape: "cyl", x: tx, y: top + 0.39 + legH + hy, z: tz, sx: tD + 0.06, sy: 0.08, sz: tD + 0.06, color: 0x3a3a38 }));
        }
      } else if (h01(i, j, 80 + k) < 0.3) {
        // antenna mast
        const ah = 4 + h01(i, j, 81 + k) * 6;
        out.push(mk({ mat: "tank", shape: "cyl", x: hx, y: top + 2.6 + ah / 2, z: hz, sx: 0.14, sy: ah, sz: 0.14, color: 0x7a7c7e }));
      }
    }
  }
  return out;
}
